import styles from './SlikerrLockup.module.css';

/**
 * The Slikerr lockup: the lotus mark above the wordmark, with the lotus
 * breathing.
 *
 * ## The whole lockup is vector, and it is IN the HTML
 *
 * Both halves are SVG. The lotus is a file (`/slikerr-mark.svg`); the wordmark
 * is the artwork's own outlines, inlined below as a single path.
 *
 * The wordmark spent a while as live Montserrat Bold text instead. That reads
 * well on paper - text selects, inherits `currentColor` and drops a request -
 * but it made the logo depend on a webfont arriving. Montserrat is loaded with
 * `display: swap`, so the first paint drew "Slikerr" in a fallback face and
 * then re-drew it in Montserrat a beat later. The owner saw exactly that and
 * described the logo as blurry and slow to settle (2026-08-24). Outlines
 * cannot do that: they are correct in the first frame or not there at all.
 *
 * Inlined rather than an `<img>` for the same reason - a file is one more
 * round trip before the logo appears, and this path is under 2 KB.
 *
 * It also means the app no longer ships a whole typeface to draw one word.
 * `SlikerrLockup` was Montserrat's ONLY consumer, so `layout.tsx` stopped
 * loading it - one fewer preloaded font on every page in the app.
 *
 * The letterforms are NOT identical to the text version: the artwork's own
 * tracking is about 5 per cent looser than Montserrat Bold at `-0.01em`, so
 * the word is a touch wider at the same height. That is the brand drawing, and
 * the text was the approximation of it.
 *
 * ## Why the lotus is still a separate file
 *
 * `slikerr-logo-ink.svg` already IS this lockup, and using it would be one
 * request and no new asset. It is not used here because the lotus has to
 * breathe ON ITS OWN: inside a single image there is no handle on the mark,
 * and scaling the whole lockup would pulse the letterforms with it, which
 * reads as the page zooming rather than the flower breathing.
 *
 * It stays an `<img>` rather than being inlined too because it carries seven
 * `id`s (a gradient and six masks), and two lockups on one page would collide
 * on them. It is 1.5 KB and now sits in server-rendered HTML, so the browser's
 * preload scanner starts it immediately.
 *
 * The lotus is `aria-hidden` and the wordmark carries the accessible name, so
 * to a screen reader this is ONE image called "Slikerr" - not a flower and a
 * word. `business-onboarding.test.tsx` asserts the count.
 */
export function SlikerrLockup() {
  return (
    <div className={styles.lockup}>
      <img src="/slikerr-mark.svg" alt="" aria-hidden="true" className={styles.lotus} />
      {/* viewBox is the measured ink box of the word - see the header of
          public/slikerr-wordmark-ink.svg. `currentColor` so the lockup takes
          the colour of whatever surface it sits on, exactly as the text did. */}
      <svg
        className={styles.wordmark}
        viewBox="10 166.88 274.26 63.43"
        role="img"
        aria-label="Slikerr"
      >
        {/* The wordmark group is a SIBLING of the lotus group in the source
            file, not a child of it - the outer translate/scale belongs to the
            lotus and putting the letters inside it drew them above the viewBox,
            where `overflow: hidden` ate them. The glyph outlines are y-up, which
            is what the large translate corrects. */}
        <g fill="currentColor" transform="translate(7.777,229.356) scale(0.07938)">
          <path d="M313 12Q229 12 152-10.50Q75-33 28-69L83-191Q128-159 189.50-138.50Q251-118 314-118Q362-118 391.50-127.50Q421-137 435-154Q449-171 449-193Q449-221 427-237.50Q405-254 369-264.50Q333-275 289.50-284.50Q246-294 202.50-308Q159-322 123-344Q87-366 64.50-402Q42-438 42-494Q42-554 74.50-603.50Q107-653 172.50-682.50Q238-712 337-712Q403-712 467-696.50Q531-681 580-650L530-527Q481-555 432-568.50Q383-582 336-582Q289-582 259-571Q229-560 216-542.50Q203-525 203-502Q203-475 225-458.50Q247-442 283-432Q319-422 362.50-412Q406-402 449.50-389Q493-376 529-354Q565-332 587.50-296Q610-260 610-205Q610-146 577-97Q544-48 478.50-18Q413 12 313 12 M886 0L730 0L730-742L886-742 M1207 0L1051 0L1051-538L1207-538L1207 0M1129-613Q1086-613 1059-638Q1032-663 1032-700Q1032-737 1059-762Q1086-787 1129-787Q1172-787 1199-763.50Q1226-740 1226-703Q1226-664 1199.50-638.50Q1173-613 1129-613 M1620-227L1501-110L1505-300L1756-538L1942-538L1701-293L1620-227M1528 0L1372 0L1372-742L1528-742L1528 0M1962 0L1773 0L1591-226L1689-347 M2314 8Q2222 8 2152.50-28Q2083-64 2045-126.50Q2007-189 2007-269Q2007-350 2044.50-412.50Q2082-475 2147-510.50Q2212-546 2294-546Q2373-546 2436.50-512.50Q2500-479 2537-416.50Q2574-354 2574-267Q2574-258 2573-246.50Q2572-235 2571-225L2134-225L2134-316L2489-316L2429-289Q2429-331 2412-362Q2395-393 2365-410.50Q2335-428 2295-428Q2255-428 2224.50-410.50Q2194-393 2177-361.50Q2160-330 2160-287L2160-263Q2160-219 2179.50-185.50Q2199-152 2234.50-134.50Q2270-117 2318-117Q2361-117 2393.50-130Q2426-143 2453-169L2536-79Q2499-37 2443-14.50Q2387 8 2314 8 M2854 0L2698 0L2698-538L2847-538L2847-386L2826-430Q2850-487 2903-516.50Q2956-546 3032-546L3032-402Q3022-403 3014-403.50Q3006-404 2997-404Q2933-404 2893.50-367.50Q2854-331 2854-254 M3305 0L3149 0L3149-538L3298-538L3298-386L3277-430Q3301-487 3354-516.50Q3407-546 3483-546L3483-402Q3473-403 3465-403.50Q3457-404 3448-404Q3384-404 3344.50-367.50Q3305-331 3305-254" />
        </g>
      </svg>
    </div>
  );
}
